import { useEffect, useState } from 'react';

import { ChainId, useEthers } from '@usedapp/core';
import { BigNumber } from 'ethers';

import { ACTIVE_ENV } from '@context/constants';
import { useVerseBalances } from '@context/providers/VerseBalancesProvider';

import { useIsLoading } from '@hooks/useIsLoading';

import { useEarnEthBalance } from './useEarnEthBalance';
import { useEarnPolBalance } from './useEarnPolBalance';

const isDev = ACTIVE_ENV === 'development' || ACTIVE_ENV === 'staging';
const MIN_VERSE = BigNumber.from('1000000').mul(BigNumber.from(10).pow(18));
const FALLBACK_BIG_NUM = BigNumber.from(0);

export const useLoungeAccess = () => {
  const verseBalances = useVerseBalances();
  const polEarnBalance = useEarnPolBalance();
  const ethEarnBalance = useEarnEthBalance();
  const isLoading = useIsLoading();
  const { account } = useEthers();
  const [meetsCriteria, setMeetsCriteria] = useState<boolean>();

  useEffect(() => {
    if (!account) {
      setMeetsCriteria(undefined);
    }
    if (
      !verseBalances ||
      !verseBalances[ChainId.Mainnet] ||
      !verseBalances[ChainId.Polygon] ||
      (isDev && !verseBalances[ChainId.Sepolia])
    ) {
      return;
    }
    const totalInEarn = polEarnBalance.add(ethEarnBalance);

    const totalVerseLiquid = (
      verseBalances[ChainId.Mainnet]?.verseLiquid ?? FALLBACK_BIG_NUM
    )
      .add(verseBalances[ChainId.Polygon]?.verseLiquid ?? FALLBACK_BIG_NUM)
      .add(
        isDev
          ? verseBalances[ChainId.Sepolia]?.verseLiquid ?? FALLBACK_BIG_NUM
          : FALLBACK_BIG_NUM,
      );

    const totalVerseInFarms = (
      verseBalances[ChainId.Mainnet]?.verseInFarms ?? FALLBACK_BIG_NUM
    )
      .add(verseBalances[ChainId.Polygon]?.verseInFarms ?? FALLBACK_BIG_NUM)
      .add(
        isDev
          ? verseBalances[ChainId.Sepolia]?.verseInFarms ?? FALLBACK_BIG_NUM
          : FALLBACK_BIG_NUM,
      );

    const totalVerseStaked = (
      verseBalances[ChainId.Mainnet]?.verseStaked ?? FALLBACK_BIG_NUM
    )
      .add(verseBalances[ChainId.Polygon]?.verseStaked ?? FALLBACK_BIG_NUM)
      .add(
        isDev
          ? verseBalances[ChainId.Sepolia]?.verseStaked ?? FALLBACK_BIG_NUM
          : FALLBACK_BIG_NUM,
      );

    setMeetsCriteria(
      totalVerseLiquid.gte(MIN_VERSE) ||
        totalVerseStaked.gte(MIN_VERSE) ||
        totalVerseInFarms.gte(MIN_VERSE) ||
        totalInEarn.gte(MIN_VERSE),
    );
  }, [verseBalances, isLoading]);

  return meetsCriteria;
};
